/** Tab bar.
 *
 *  Unifies four independent implementations that used three different active
 *  colours (border-blue-600/700 with text-blue-600/700), none of which were
 *  themed.
 */
import React from "react";

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  /** Optional trailing count, e.g. "Data Sources (3)". */
  count?: number;
}

interface TabsProps<T extends string> {
  tabs: readonly TabItem<T>[];
  active: T;
  onChange: (id: T) => void;
  className?: string;
}

export default function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  className = "",
}: TabsProps<T>) {
  return (
    <div className={`border-b border-edge ${className}`}>
      <nav className="-mb-px flex flex-wrap gap-1" role="tablist">
        {tabs.map((tab) => {
          const isActive = tab.id === active;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(tab.id)}
              className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors duration-100 ${
                isActive
                  ? "border-accent text-accent"
                  : "border-transparent text-content-muted hover:border-edge-strong hover:text-content"
              }`}
            >
              {tab.label}
              {typeof tab.count === "number" ? (
                <span className={isActive ? "ml-1.5 text-accent" : "ml-1.5 text-content-subtle"}>
                  ({tab.count})
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
